import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import apiReq from 'core/api';
import { uploadFileChunked, PROCESS_TYPE_CONTEXT_DOCUMENT_PARSING } from 'utils/chunkedFileUpload';
import { processContextDocumentParsing } from 'modules/estimation/api';
import { contextDocumentEvents } from '../utils/contextDocumentEvents';
import type { ContextFileSelection } from 'types/contextDocuments';

export type DraftStatus = 'uploading' | 'parsing' | 'ready' | 'error';

export interface AttachmentDraft {
  localId: string;
  name: string;
  mimeType: string;
  size: number;
  status: DraftStatus;
  progress: number;
  /** Текстовая стадия парсинга из stream-тиков (например, «Чанкинг»). */
  progressLabel?: string;
  fileId?: string;
  processId?: string;
  documentId?: string;
  error?: string;
  /** Выбранные заголовки превью; undefined = весь документ */
  selectedHeaders?: string[];
}

export interface UseMessageAttachmentsDraftResult {
  drafts: AttachmentDraft[];
  attachFile: (file: File) => void;
  removeDraft: (localId: string) => void;
  clear: () => void;
  getReadyIds: () => string[];
  hasPending: () => boolean;
  setDraftHeaders: (localId: string, headers: string[] | undefined) => void;
  getReadySelections: () => ContextFileSelection[];
}

const makeLocalId = (): string => `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export function useMessageAttachmentsDraft(
  projectId: string,
  chatId: string | null
): UseMessageAttachmentsDraftResult {
  const [drafts, setDrafts] = useState<AttachmentDraft[]>([]);
  const queryClient = useQueryClient();
  const draftsRef = useRef<AttachmentDraft[]>([]);
  const chatIdRef = useRef<string | null>(chatId);

  useEffect(() => {
    draftsRef.current = drafts;
  }, [drafts]);

  useEffect(() => {
    if (chatIdRef.current !== chatId) {
      chatIdRef.current = chatId;
      setDrafts([]);
    }
  }, [chatId]);

  useEffect(() => {
    const off = contextDocumentEvents.on((payload) => {
      setDrafts((prev) => {
        const idx = prev.findIndex((d) => d.processId && d.processId === payload.process_id);
        if (idx === -1) return prev;
        const current = prev[idx];
        let next: AttachmentDraft;
        if (payload.status === 'success') {
          next = {
            ...current,
            status: 'ready',
            documentId: payload.document_id,
            progressLabel: undefined,
          };
        } else if (payload.status === 'error') {
          next = {
            ...current,
            status: 'error',
            error: payload.message || 'Ошибка парсинга',
            progressLabel: undefined,
          };
        } else if (payload.status === 'stream') {
          const label = payload.progress?.label?.trim();
          // Пустые тики бэк не шлёт, но на всякий случай не сбрасываем уже показанную стадию.
          if (!label) return prev;
          next = { ...current, status: 'parsing', progressLabel: label };
        } else {
          return prev;
        }
        const updated = [...prev];
        updated[idx] = next;
        return updated;
      });

      if (payload.status === 'success' && chatIdRef.current) {
        queryClient.invalidateQueries({
          queryKey: ['contextDocuments', projectId, chatIdRef.current],
        });
      }
    });
    return off;
  }, [projectId, queryClient]);

  const attachFile = useCallback(
    (file: File) => {
      if (!chatId) return;
      const localId = makeLocalId();
      const initial: AttachmentDraft = {
        localId,
        name: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
        status: 'uploading',
        progress: 0,
      };
      setDrafts((prev) => [...prev, initial]);

      const updateDraft = (patch: Partial<AttachmentDraft>) => {
        setDrafts((prev) => prev.map((d) => (d.localId === localId ? { ...d, ...patch } : d)));
      };

      (async () => {
        try {
          const fileId = await uploadFileChunked(
            file,
            projectId,
            PROCESS_TYPE_CONTEXT_DOCUMENT_PARSING,
            (percent) => updateDraft({ progress: percent })
          );

          updateDraft({ fileId, status: 'parsing', progress: 100 });

          const { process_id } = await processContextDocumentParsing(fileId, 'chat', chatId);

          updateDraft({ processId: process_id });
        } catch (e) {
          updateDraft({
            status: 'error',
            error: e instanceof Error ? e.message : 'Ошибка загрузки',
          });
        }
      })();
    },
    [projectId, chatId]
  );

  const removeDraft = useCallback((localId: string) => {
    const target = draftsRef.current.find((d) => d.localId === localId);
    if (target && target.status === 'uploading' && target.fileId) {
      apiReq('file/upload/abort', 'POST', { file_id: target.fileId }).catch(() => undefined);
    }
    setDrafts((prev) => prev.filter((d) => d.localId !== localId));
  }, []);

  const clear = useCallback(() => {
    setDrafts([]);
  }, []);

  const getReadyIds = useCallback(
    () =>
      drafts.filter((d) => d.status === 'ready' && d.documentId).map((d) => d.documentId as string),
    [drafts]
  );

  const hasPending = useCallback(
    () => drafts.some((d) => d.status === 'uploading' || d.status === 'parsing'),
    [drafts]
  );

  const setDraftHeaders = useCallback((localId: string, headers: string[] | undefined) => {
    setDrafts((prev) =>
      prev.map((d) =>
        d.localId === localId
          ? { ...d, selectedHeaders: headers && headers.length > 0 ? headers : undefined }
          : d
      )
    );
  }, []);

  const getReadySelections = useCallback(
    (): ContextFileSelection[] =>
      drafts
        .filter((d) => d.status === 'ready' && d.documentId)
        .map((d) =>
          d.selectedHeaders && d.selectedHeaders.length > 0
            ? { id: d.documentId as string, headers: d.selectedHeaders }
            : { id: d.documentId as string }
        ),
    [drafts]
  );

  return {
    drafts,
    attachFile,
    removeDraft,
    clear,
    getReadyIds,
    hasPending,
    setDraftHeaders,
    getReadySelections,
  };
}
